<template>
  <div class="flex h-screen">
    <!-- Wizard obligatorio (solo agentes master, no asociados) -->
    <AgentOnboardingWizard />

    <!-- Sidebar de Escritorio Mejorada -->
    <div class="hidden md:flex flex-col w-80 fixed left-0 top-16 h-[calc(100vh-4rem)] z-50">
      

      <!-- Navegación extraída al componente -->
      <!-- OCULTO: Props y eventos de contratos tradicionales eliminados -->
      <AgentSideNavbar
        :total-contracts="totalContracts"
        :total-colivings="totalColivings"
        :pre-assigned-users="preAssignedUsers"
        :total-interested="totalInterested"
        :highlighted-colivings="highlightedColivings"
        :pending-verifications-list="pendingVerificationsList"
        :detailed-recent-events="detailedRecentEvents"
        :recent-events="recentEvents"
        :dismissed-events-count="dismissedEvents.size"
        :clients="clients"
        :user-verification-data="userVerificationData"
        :profile-incomplete="profileIncomplete"
        @go-to-coliving="handleGoToColiving"
        @go-to-verifications="handleGoToVerifications"
        @dismiss-event="dismissEvent"
        @clear-dismissed-events="clearDismissedEvents"
        @logout="logout"
      />
    </div>

    <!-- Contenedor Principal -->
    <div class="flex-1 flex flex-col overflow-hidden ml-0 md:ml-80 pt-2">
      <AgentMobileNavbar class="md:hidden" />

      <!-- Banner: perfil incompleto (oculto mientras el wizard obligatorio está activo) -->
      <div
        v-if="profileIncomplete && !showOnboardingWizard"
        class="mx-4 mt-3 mb-1 flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-sm"
      >
        <svg class="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
        </svg>
        <span class="flex-1 text-amber-800">
          <strong>Completa tu perfil</strong> — añade tu DNI/CIF y nombre de agencia para poder gestionar contratos.
        </span>
        <router-link
          to="/agent-dashboard/profile"
          class="flex-shrink-0 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-semibold hover:bg-amber-600 transition-colors"
        >
          Completar →
        </router-link>
      </div>

      <main
        class="flex-1 overflow-x-hidden overflow-y-auto sm:p-0 md:p-0 lg:p-0 no-scrollbar pb-16 md:pb-0"
        :class="{ 'pt-14': isOnWizardStepRoute }"
      >
        <router-view 
          v-if="route.name === 'AgentHome'"
          :highlighted-colivings="highlightedColivings"
          :detailed-recent-events="detailedRecentEvents"
          :clients="clients"
          :user-verification-data="userVerificationData"
          :profile-incomplete="profileIncomplete"
          :colivings="colivings"
          :contracts="contracts"
          :custom-events="customCalendarEvents"
        />
        <router-view 
          v-else 
          :profile-incomplete="profileIncomplete"
        />
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { getAuth, signOut } from 'firebase/auth'

import AgentMobileNavbar from './AgentMobileNavbar.vue'
import AgentSideNavbar from './AgentSideNavbar.vue'
import AgentOnboardingWizard from './AgentOnboardingWizard.vue'
import { useSharedAgentOnboarding } from '@/composables/useAgentOnboarding'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const auth = getAuth()

const {
  showWizard: showOnboardingWizard,
  wizardStep,
  initOnboardingAuth,
  destroyOnboardingAuth,
  fetchOnboardingStatus,
} = useSharedAgentOnboarding()

const WIZARD_STEP_ROUTES = {
  profile: '/agent-dashboard/profile',
  firstColiving: '/agent-dashboard/addColiving',
  published: '/agent-dashboard/colivings',
}

const isOnWizardStepRoute = computed(() => {
  if (!showOnboardingWizard.value || !wizardStep.value) return false
  const stepRoute = WIZARD_STEP_ROUTES[wizardStep.value]
  if (!stepRoute) return false
  return route.path === stepRoute || route.path.startsWith(`${stepRoute}/`)
})

// Estados para los datos
const contracts = ref([])
const colivings = ref([])
const clients = ref([])
const isLoadingData = ref(false)

// NUEVO: Estados para información de verificación de usuarios
const userVerificationData = ref(new Map()) // Map userId -> verificationStatus

// NUEVO: Estado para eventos eliminados
const dismissedEvents = ref(new Set())

// NUEVO: Estado para eventos personalizados del calendario
const customCalendarEvents = ref([])

// Estado para verificación de perfil completo
const profileIncomplete = ref(false)

const logout = async () => {
  try {
    const auth = getAuth()
    await signOut(auth)
    router.push('/login')
  } catch (error) {
    console.error('Error al cerrar sesión:', error)
  }
}

// Computed properties para estadísticas
// OCULTO: Contratos tradicionales deshabilitados para agentes
// const totalContracts = computed(() => contracts.value.length || 0)
const totalContracts = computed(() => 0) // Siempre 0 - contratos tradicionales ocultos
const totalColivings = computed(() => colivings.value.length || 0)

const preAssignedUsers = computed(() => {
  // Contar aplicaciones de colivings en todos los estados en curso
  let count = 0
  colivings.value.forEach(coliving => {
    if (coliving.applications && Array.isArray(coliving.applications)) {
      const inProgressStatuses = ['pre_approved', 'owner_signed', 'applicant_signed', 'contract_signed', 'payment_completed'];
      count += coliving.applications.filter(app => 
        inProgressStatuses.includes(app.status)
      ).length
    }
  })
  return count
})

const totalInterested = computed(() => {
  // OCULTO: Interesados de contratos tradicionales deshabilitados
  // const contractsInterested = contracts.value.reduce((total, contract) => {
  //   return total + (contract.interestedApplicants?.length || 0)
  // }, 0)
  
  // NUEVO: Solicitudes de colivings
  const colivingsApplications = colivings.value.reduce((total, coliving) => {
    return total + (coliving.pendingApplications || 0)
  }, 0)
  
  // return contractsInterested + colivingsApplications
  return colivingsApplications // Solo colivings
})

// OCULTO: Lista de contratos tradicionales deshabilitados
// const contractsWithStats = computed(() => {
//   return contracts.value.map(contract => {
//     const interestedCount = contract.interestedApplicants?.length || 0
//     
//     // Calcular la oferta más alta
//     let highestOffer = null
//     if (contract.interestedApplicants && contract.interestedApplicants.length > 0) {
//       const offers = contract.interestedApplicants
//         .map(applicant => applicant.offerAmount)
//         .filter(offer => offer && !isNaN(offer))
//       if (offers.length > 0) {
//         highestOffer = Math.max(...offers)
//       }
//     }
//     
//     // Calcular días hasta vencimiento (solo para alquileres)
//     let daysUntilExpiry = null
//     if (contract.contractType === 'rent' && contract.endDate) {
//       const endDate = new Date(contract.endDate)
//       const now = new Date()
//       const diffTime = endDate - now
//       daysUntilExpiry = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
//     }
//     
//     return {
//       ...contract,
//       interestedCount,
//       highestOffer,
//       daysUntilExpiry
//     }
//   }).sort((a, b) => {
//     // Ordenar por número de interesados (descendente) y luego por fecha de creación
//     if (b.interestedCount !== a.interestedCount) {
//       return b.interestedCount - a.interestedCount
//     }
//     return new Date(b.createdAt) - new Date(a.createdAt)
//   }).slice(0, 10) // Limitar a los 10 más relevantes
// })
const contractsWithStats = computed(() => []) // Siempre vacío - contratos tradicionales ocultos

// Eventos recientes detallados (similar a ManageSidebar)
const detailedRecentEvents = computed(() => {
  const events = []
  
  // OCULTO: Eventos de contratos tradicionales deshabilitados
  // contracts.value.forEach(contract => {
  //   if (contract.assignedApplicant && contract.ownerSigned && contract.applicantSigned) {
  //     events.push({
  //       id: `signed-${contract.id}`,
  //       type: 'signed',
  //       title: 'Contrato firmado',
  //       description: `${contract.assignedApplicant.name} - ${contract.title}`,
  //       date: contract.updatedAt || contract.createdAt
  //     })
  //   }
  //   
  //   // Eventos de asignación
  //   if (contract.assignedApplicant && (!contract.ownerSigned || !contract.applicantSigned)) {
  //     events.push({
  //       id: `assigned-${contract.id}`,
  //       type: 'assigned',
  //       title: 'Contrato asignado',
  //       description: `${contract.assignedApplicant.name} - ${contract.title}`,
  //       date: contract.assignedApplicant.assignedAt || contract.updatedAt || contract.createdAt
  //     })
  //   }
  //   
  //   // Eventos de interés (solo los más recientes)
  //   if (contract.interestedApplicants && contract.interestedApplicants.length > 0) {
  //     contract.interestedApplicants
  //       .slice(-2) // Solo los 2 más recientes por contrato
  //       .forEach(applicant => {
  //         if (applicant.expressedInterestAt) {
  //           events.push({
  //             id: `interest-${contract.id}-${applicant.userId}`,
  //             type: 'interest',
  //             title: 'Nuevo interesado',
  //             description: `${applicant.name || applicant.email} - ${contract.title}`,
  //             date: applicant.expressedInterestAt
  //           })
  //         }
  //       })
  //   }
  // })
  
  // Eventos de colivings
  colivings.value.forEach(coliving => {
    // Eventos de creación de coliving
    if (coliving.createdAt) {
      events.push({
        id: `coliving-created-${coliving.id}`,
        type: 'coliving',
        title: 'Coliving creado',
        description: `${coliving.title} - ${coliving.address}`,
        date: coliving.createdAt
      })
    }
    
    // NUEVO: Eventos de aplicaciones de colivings (sincronizado con notificationService)
    if (coliving.applications && coliving.applications.length > 0) {
      // PRIMERO: Procesar aplicaciones retiradas para crear eventos de notificación
      coliving.applications
        .filter(application => application.status === 'retired')
        .forEach(application => {
          if (application.retiredAt || application.updatedAt) {
            events.push({
              id: `coliving-app-retired-${application.id}`,
              type: 'coliving_application_retired',
              title: 'Solicitud de coliving retirada',
              description: `${application.applicantName} retiró su solicitud para ${coliving.title}`,
              date: application.retiredAt || application.updatedAt
            })
          }
        })
      
      // LUEGO: Procesar aplicaciones activas (filtrar aplicaciones retiradas para que no generen otros eventos)
      coliving.applications
        .filter(application => application.status !== 'retired')
        .forEach(application => {
        // 1. Solicitud enviada - MEJORADO con información detallada
        if (application.status === 'submitted' && application.createdAt) {
          // Calcular duración en días si tenemos fechas
          let durationDays = null;
          if (application.desiredStartDate && application.desiredEndDate) {
            const startDate = application.desiredStartDate.toDate ? application.desiredStartDate.toDate() : new Date(application.desiredStartDate);
            const endDate = application.desiredEndDate.toDate ? application.desiredEndDate.toDate() : new Date(application.desiredEndDate);
            if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
              durationDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
            }
          }

          events.push({
            id: `coliving-app-submitted-${application.id}`,
            type: 'coliving_application',
            title: `Nueva solicitud de coliving - ${coliving.title}`,
            description: `${application.applicantName} solicita habitación en ${coliving.title}`,
            date: application.createdAt,
            // NUEVO: Información extendida para mostrar en el resumen
            applicationData: {
              id: application.id,
              applicantId: application.applicantId,
              applicantName: application.applicantName,
              applicantEmail: application.applicantEmail,
              applicantPhone: application.applicantPhone,
              stayReason: application.stayReason,
              stayDurationMonths: application.stayDurationMonths,
              desiredStartDate: application.desiredStartDate,
              desiredEndDate: application.desiredEndDate,
              durationDays: durationDays,
              message: application.message,
              roomPreferences: application.roomPreferences,
              colivingId: coliving.id,
              colivingTitle: coliving.title,
              status: application.status
            }
          })
        }
        
        // 2. Pre-aprobación y firma (estados intermedios antes del pago)
        const preApprovalAndSigningStatuses = ['pre_approved', 'owner_signed', 'applicant_signed', 'contract_signed'];
        const signingEventDate = application.applicantSignedAt || application.ownerSignedAt || application.preApprovedAt;

        if (preApprovalAndSigningStatuses.includes(application.status) && signingEventDate) {
          // Calcular duración en días si tenemos fechas
          let durationDays = null;
          if (application.desiredStartDate && application.desiredEndDate) {
            const startDate = application.desiredStartDate.toDate ? application.desiredStartDate.toDate() : new Date(application.desiredStartDate);
            const endDate = application.desiredEndDate.toDate ? application.desiredEndDate.toDate() : new Date(application.desiredEndDate);
            if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
              durationDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
            }
          }

          const roomInfo = application.assignedRoomData ? 
            (application.assignedRoomData.title || `Habitación ${application.assignedRoomData.number}`) : 
            'Habitación asignada';
          
          let eventTitle = `Solicitud en progreso - ${coliving.title}`;
          if (application.status === 'pre_approved') eventTitle = `Solicitud pre-asignada - ${coliving.title}`;
          if (application.status === 'contract_signed') eventTitle = `Contrato firmado - ${coliving.title}`;

          events.push({
            id: `coliving-pre-approved-${application.id}`,
            type: 'coliving_pre_approved', // Usamos el mismo tipo para la vista expandida
            title: eventTitle,
            description: `${application.applicantName} - ${roomInfo} en ${coliving.title}`,
            date: signingEventDate,
            applicationData: {
              id: application.id,
              applicantId: application.applicantId,
              applicantName: application.applicantName,
              applicantEmail: application.applicantEmail,
              applicantPhone: application.applicantPhone,
              stayReason: application.stayReason,
              stayDurationMonths: application.stayDurationMonths,
              desiredStartDate: application.desiredStartDate,
              desiredEndDate: application.desiredEndDate,
              durationDays: durationDays,
              message: application.message,
              roomPreferences: application.roomPreferences,
              assignedRoomData: application.assignedRoomData,
              colivingId: coliving.id,
              colivingTitle: coliving.title,
              status: application.status
            }
          })
        }
        
        // 3. Inquilino firmó contrato (evento simple)
        if (application.applicantSigned && application.applicantSignedAt) {
          events.push({
            id: `coliving-applicant-signed-${application.id}`,
            type: 'coliving_signed',
            title: 'Contrato de coliving firmado',
            description: `${application.applicantName} firmó el contrato para ${coliving.title}`,
            date: application.applicantSignedAt
          })
        }
        
        // 4. Agente/Propietario firmó contrato (evento simple)
        if (application.ownerSigned && application.ownerSignedAt) {
          events.push({
            id: `coliving-owner-signed-${application.id}`,
            type: 'coliving_signed',
            title: 'Has firmado contrato de coliving',
            description: `Contrato firmado para ${application.applicantName} en ${coliving.title}`,
            date: application.ownerSignedAt
          })
        }
        
        // 5. Pago completado (con datos para vista expandida)
        if (application.status === 'payment_completed' && application.paymentCompletedAt) {
          events.push({
            id: `coliving-payment-completed-${application.id}`,
            type: 'coliving_payment',
            title: 'Pago de coliving completado',
            description: `${application.applicantName} completó el pago para ${coliving.title}`,
            date: application.paymentCompletedAt,
            applicationData: {
              id: application.id,
              applicantId: application.applicantId,
              applicantName: application.applicantName,
              applicantEmail: application.applicantEmail,
              applicantPhone: application.applicantPhone,
              stayReason: application.stayReason,
              stayDurationMonths: application.stayDurationMonths,
              desiredStartDate: application.desiredStartDate,
              desiredEndDate: application.desiredEndDate,
              assignedRoomData: application.assignedRoomData,
              colivingId: coliving.id,
              colivingTitle: coliving.title,
              status: application.status
            }
          })
        }
        
        // 6. Asignación final confirmada
        if (application.status === 'room_assigned' && application.assignmentConfirmedAt) {
          // Calcular duración en días si tenemos fechas
          let durationDays = null;
          if (application.desiredStartDate && application.desiredEndDate) {
            const startDate = application.desiredStartDate.toDate ? application.desiredStartDate.toDate() : new Date(application.desiredStartDate);
            const endDate = application.desiredEndDate.toDate ? application.desiredEndDate.toDate() : new Date(application.desiredEndDate);
            if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
              durationDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
            }
          }

          const roomInfo = application.assignedRoomData ? 
            (application.assignedRoomData.title || `Habitación ${application.assignedRoomData.number}`) : 
            'Habitación asignada'
          events.push({
            id: `coliving-assignment-confirmed-${application.id}`,
            type: 'coliving_assignment',
            title: `Asignación de habitación confirmada - ${coliving.title}`,
            description: `${application.applicantName} - ${roomInfo} en ${coliving.title}`,
            date: application.assignmentConfirmedAt,
            // NUEVO: Información extendida para mostrar en el resumen
            applicationData: {
              id: application.id,
              applicantId: application.applicantId,
              applicantName: application.applicantName,
              applicantEmail: application.applicantEmail,
              applicantPhone: application.applicantPhone,
              stayReason: application.stayReason,
              stayDurationMonths: application.stayDurationMonths,
              desiredStartDate: application.desiredStartDate,
              desiredEndDate: application.desiredEndDate,
              durationDays: durationDays,
              message: application.message,
              roomPreferences: application.roomPreferences,
              assignedRoomData: application.assignedRoomData,
              colivingId: coliving.id,
              colivingTitle: coliving.title,
              status: application.status
            }
          })
        }
        
        // 7. Aplicación rechazada
        if (application.status === 'rejected' && application.updatedAt) {
          events.push({
            id: `coliving-rejected-${application.id}`,
            type: 'coliving_rejected',
            title: 'Solicitud de coliving rechazada',
            description: `Solicitud de ${application.applicantName} rechazada para ${coliving.title}`,
            date: application.updatedAt
          })
        }
        
        // 8. Aplicación terminada
        if (application.status === 'terminated' && application.updatedAt) {
          events.push({
            id: `coliving-terminated-${application.id}`,
            type: 'coliving_terminated',
            title: 'Estancia de coliving terminada',
            description: `${application.applicantName} terminó su estancia en ${coliving.title}`,
            date: application.updatedAt
          })
        }
      })
    }
    
    // LEGACY: Eventos de habitaciones con interesados (sistema antiguo)
    if (coliving.rooms) {
      coliving.rooms.forEach(room => {
        if (room.interestedApplicants && room.interestedApplicants.length > 0) {
          room.interestedApplicants.slice(-1).forEach(applicant => {
            if (applicant.expressedInterestAt) {
              events.push({
                id: `room-interest-${room.id}-${applicant.userId}`,
                type: 'interest',
                title: 'Interés en habitación (legacy)',
                description: `${applicant.name || applicant.email} - ${room.title || 'Habitación'} en ${coliving.title}`,
                date: applicant.expressedInterestAt
              })
            }
          })
        }
      })
    }
  })
  
  // Eventos de verificaciones
  clients.value.forEach(client => {
    if (client.verified) {
      events.push({
        id: `verified-${client.id}`,
        type: 'verification',
        title: 'Cliente verificado',
        description: `${client.fullName} ha sido verificado`,
        date: client.verifiedAt || client.updatedAt || client.createdAt
      })
    }
  })
  
  // Filtrar, ordenar y limitar eventos (NUEVO: filtrar eventos eliminados)
  return events
    .filter(event => {
      // Filtrar eventos eliminados
      if (dismissedEvents.value.has(event.id)) return false
      
      if (!event.date) return false
      try {
        const dateObj = new Date(event.date)
        return !isNaN(dateObj.getTime())
      } catch {
        return false
      }
    })
    .sort((a, b) => {
      try {
        return new Date(b.date) - new Date(a.date)
      } catch {
        return 0
      }
    })
    .slice(0, 8) // Mostrar los 8 eventos más recientes
})

// Propiedades destacadas (con más interesados)
const highlightedContracts = computed(() => {
  return [...contracts.value]
    .filter(contract => contract.interestedApplicants?.length > 0)
    .sort((a, b) => (b.interestedApplicants?.length || 0) - (a.interestedApplicants?.length || 0))
    .slice(0, 3)
})

// Colivings destacados (con más habitaciones)
const highlightedColivings = computed(() => {
  return [...colivings.value]
    .sort((a, b) => {
      const aRooms = a.rooms?.length || 0
      const bRooms = b.rooms?.length || 0
      return bRooms - aRooms
    })
    .slice(0, 3)
})

// Lista de verificaciones pendientes
const pendingVerificationsList = computed(() => {
  return clients.value
    .filter(client => !client.verified && client.verificationStatus !== 'rejected')
    .slice(0, 3)
})

// Eventos recientes combinados
const recentEvents = computed(() => {
  const events = []
  
  // Eventos de contratos
  contracts.value.forEach(contract => {
    if (contract.interestedApplicants && contract.interestedApplicants.length > 0) {
      contract.interestedApplicants
        .slice(-1) // Último por contrato
        .forEach(applicant => {
          if (applicant.expressedInterestAt) {
            events.push({
              id: `interest-${contract.id}-${applicant.userId}`,
              type: 'interest',
              title: 'Nuevo interesado',
              description: `${applicant.name || applicant.email} - ${contract.title}`,
              date: applicant.expressedInterestAt
            })
          }
        })
    }

    if (contract.assignedApplicant) {
      events.push({
        id: `assigned-${contract.id}`,
        type: 'contract',
        title: 'Contrato asignado',
        description: `${contract.assignedApplicant.name} - ${contract.title}`,
        date: contract.assignedApplicant.assignedAt || contract.updatedAt || contract.createdAt
      })
    }
  })

  // Eventos de colivings
  colivings.value.forEach(coliving => {
    if (coliving.rooms) {
      coliving.rooms.forEach(room => {
        if (room.interestedApplicants && room.interestedApplicants.length > 0) {
          room.interestedApplicants.slice(-1).forEach(applicant => {
            if (applicant.expressedInterestAt) {
              events.push({
                id: `room-interest-${room.id}-${applicant.userId}`,
                type: 'interest',
                title: 'Interesado en habitación',
                description: `${applicant.name || applicant.email} - ${room.title || 'Habitación'} en ${coliving.title}`,
                date: applicant.expressedInterestAt
              })
            }
          })
        }
      })
    }
  })

  // Eventos de verificaciones
  clients.value.forEach(client => {
    if (!client.verified && client.verificationStatus !== 'rejected') {
      events.push({
        id: `verification-${client.id}`,
        type: 'verification',
        title: 'Verificación pendiente',
        description: `${client.fullName} requiere verificación`,
        date: client.createdAt
      })
    }
  })

  // Filtrar y ordenar eventos
  return events
    .filter(event => {
      // Filtrar eventos eliminados
      if (dismissedEvents.value.has(event.id)) return false
      
      if (!event.date) return false
      try {
        const dateObj = new Date(event.date)
        return !isNaN(dateObj.getTime())
      } catch {
        return false
      }
    })
    .sort((a, b) => {
      try {
        return new Date(b.date) - new Date(a.date)
      } catch {
        return 0
      }
    })
    .slice(0, 10)
})

// Funciones helper conservadas para las computed properties
const getClientName = (clientId) => {
  if (!clientId) return 'Cliente no encontrado'
  const client = clients.value.find(c => c.id === clientId || c.firebaseAuthUid === clientId)
  return client ? client.fullName : 'Cliente no encontrado'
}

const getColivingClientName = (coliving) => {
  if (coliving.agentIsOwner) {
    return 'Tú (Agente)'
  }
  return getClientName(coliving.clientId)
}

const getOccupiedRoomsCount = (coliving) => {
  if (!coliving.rooms || !Array.isArray(coliving.rooms)) {
    return 0
  }
  return coliving.rooms.filter(room => room.status === 'ocupada').length
}

// Funciones para cargar datos
const fetchAgentData = async () => {
  if (isLoadingData.value) return
  
  try {
    isLoadingData.value = true
    const currentUser = auth.currentUser
    
    if (!currentUser) {
      console.warn('No hay usuario autenticado para cargar datos del agente')
      return
    }

    // Todos resuelven effectiveAgentId vía API
    const [colivingsResult, clientsResult, eventsResult] = await Promise.allSettled([
      fetchColivings(),
      fetchClients(),
      fetchCustomCalendarEvents()
    ])

    // OCULTO: Contratos tradicionales deshabilitados
    // if (contractsResult.status === 'fulfilled') {
    //   contracts.value = contractsResult.value
    // } else {
    //   console.error('Error cargando contratos:', contractsResult.reason)
    //   contracts.value = []
    // }
    contracts.value = [] // Siempre vacío

    if (colivingsResult.status === 'fulfilled') {
      colivings.value = colivingsResult.value
    } else {
      console.error('Error cargando colivings:', colivingsResult.reason)
      colivings.value = []
    }

    if (clientsResult.status === 'fulfilled') {
      clients.value = clientsResult.value
    } else {
      console.error('Error cargando clientes:', clientsResult.reason)
      clients.value = []
    }

    if (eventsResult.status === 'fulfilled') {
      customCalendarEvents.value = eventsResult.value
    } else {
      console.error('Error cargando eventos personalizados:', eventsResult.reason)
      customCalendarEvents.value = []
    }
  } catch (error) {
    console.error('Error general al cargar datos del agente:', error)
  } finally {
    isLoadingData.value = false
  }
}

// OCULTO: Contratos tradicionales deshabilitados
// const fetchContracts = async (agentId) => {
//   const contractsQuery = query(
//     collection(db, 'contracts'),
//     where('agentId', '==', agentId)
//   )
//   
//   const querySnapshot = await getDocs(contractsQuery)
//   return querySnapshot.docs.map(doc => ({
//     id: doc.id,
//     ...doc.data(),
//     createdAt: doc.data().createdAt?.toDate() || new Date(),
//   }))
// }

// Convierte un valor de fecha (string ISO, Date, o nulo) a un objeto Date
const safeToDate = (v) => {
  if (!v) return null
  if (v instanceof Date) return v
  const d = new Date(v)
  return isNaN(d.getTime()) ? null : d
}

const fetchColivings = async () => {
  const user = auth.currentUser
  if (!user) return []
  const token = await user.getIdToken()

  // 1. Obtener lista de colivings activos del agente (resuelve effectiveAgentId en servidor)
  const colivingsRes = await fetch('/api/agents/me/colivings', {
    headers: { Authorization: `Bearer ${token}` }
  })
  if (!colivingsRes.ok) return []
  const { data: colivingsList } = await colivingsRes.json()
  if (!colivingsList || colivingsList.length === 0) return []

  // 2. Obtener aplicaciones de cada coliving en paralelo
  const colivingsWithApps = await Promise.all(
    colivingsList.map(async (col) => {
      let applications = []
      try {
        const appsRes = await fetch(`/api/colivings/${col.id}/applications`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (appsRes.ok) {
          const raw = await appsRes.json()
          // El endpoint devuelve un array directamente
          applications = (Array.isArray(raw) ? raw : raw.applications || []).map(app => ({
            ...app,
            createdAt: safeToDate(app.createdAt) || new Date(),
            updatedAt: safeToDate(app.updatedAt),
            preApprovedAt: safeToDate(app.preApprovedAt),
            ownerSignedAt: safeToDate(app.ownerSignedAt),
            applicantSignedAt: safeToDate(app.applicantSignedAt),
            paymentCompletedAt: safeToDate(app.paymentCompletedAt),
            assignmentConfirmedAt: safeToDate(app.assignmentConfirmedAt),
          }))
        }
      } catch {
        // Si falla un coliving, continuamos con el resto
      }

      const pendingApplicationsCount = applications.filter(app =>
        ['submitted', 'pending', 'under_review'].includes(app.status)
      ).length

      const rooms = (col.roomsConfiguration || col.rooms || []).map(room => ({
        ...room,
        status: room.status || 'libre',
        currentTenant: room.currentTenant || null,
        occupiedUntil: room.occupiedUntil || null,
        comments: room.comments || '',
        title: `Habitación ${room.number}`,
        propertyPrice: room.price,
        propertySize: room.size,
        parentColivingId: col.id,
        parentColivingTitle: col.title,
      }))

      return {
        ...col,
        createdAt: safeToDate(col.createdAt) || new Date(),
        rooms,
        applications,
        pendingApplications: pendingApplicationsCount,
      }
    })
  )

  return colivingsWithApps
}

const fetchClients = async () => {
  try {
    const user = auth.currentUser
    if (!user) return []
    const token = await user.getIdToken()
    const response = await fetch('/api/agents/me/managed-owners', {
      headers: { Authorization: `Bearer ${token}` }
    })
    if (!response.ok) return []
    const { data } = await response.json()
    return (data || []).map(owner => ({
      ...owner,
      createdAt: owner.createdAt ? new Date(owner.createdAt) : new Date()
    }))
  } catch (error) {
    return []
  }
}

// NUEVA: Función para cargar eventos personalizados del calendario
const fetchCustomCalendarEvents = async () => {
  try {
    const user = auth.currentUser
    if (!user) return []
    const token = await user.getIdToken()
    const response = await fetch('/api/agents/me/calendar-events', {
      headers: { Authorization: `Bearer ${token}` }
    })
    if (!response.ok) return []
    const { data } = await response.json()
    return (data || []).map(event => {
      const safeDate = (v) => {
        if (!v) return new Date()
        const d = new Date(v)
        return isNaN(d.getTime()) ? new Date() : d
      }
      return {
        ...event,
        date: safeDate(event.date),
        createdAt: safeDate(event.createdAt)
      }
    })
  } catch (error) {
    console.error('Error cargando eventos personalizados del calendario:', error)
    return []
  }
}

// NUEVA: Función para cargar información de verificación de usuarios
const loadUserVerificationData = async (userIds) => {
  if (!userIds || userIds.length === 0) return

        const { doc: firestoreDoc, getDoc } = await import('firebase/firestore')
      const { db } = await import('../../firebase')
      const { getStorage, ref: storageRef, listAll } = await import('firebase/storage')
  const storage = getStorage()

  const verificationMap = new Map()

  for (const userId of userIds) {
    if (!userId || userVerificationData.value.has(userId)) continue

    try {
      // Obtener datos del perfil del usuario
      const userDoc = await getDoc(firestoreDoc(db, 'users', userId))
      
      if (userDoc.exists()) {
        const userData = userDoc.data()
        
        // Verificar perfil completado (identidad: DNI y fotos)
        const hasCompletedProfile = !!(
          userData.dni && 
          userData.dniFrontPictureUrl 
        )
        
        // Verificar documentos esenciales
        const documentTypes = ['payslips', 'work_life_report', 'tax_declaration', 'employment_contract', 'additional_docs']
        let hasDocuments = false
        
        for (const docType of documentTypes) {
          try {
            const folderRef = storageRef(storage, `essential_documents/${userId}/${docType}/`)
            const listResult = await listAll(folderRef)
            if (listResult.items.length > 0) {
              hasDocuments = true
              break
            }
          } catch (docError) {
            // Continuar con el siguiente tipo
          }
        }
        
        // Información del avalista
        const hasGuarantor = userData.hasGuarantor || false
        const guarantorInfo = userData.guarantorInfo || null
        
        // Guardar con EXACTAMENTE la misma estructura que ColivingDetailsView.vue
        verificationMap.set(userId, {
          profile: userData, // ✅ AÑADIDO: Perfil completo del usuario
          hasCompletedProfile,
          hasEssentialDocuments: hasDocuments,
          hasGuarantor,
          guarantorInfo
        })
      } else {
        // Usuario no encontrado
        verificationMap.set(userId, {
          profile: null, // ✅ AÑADIDO: Sin perfil si no existe
          hasCompletedProfile: false,
          hasEssentialDocuments: false,
          hasGuarantor: false,
          guarantorInfo: null
        })
      }
    } catch (error) {
      console.warn(`[AgentLayout] Error cargando verificación para usuario ${userId}:`, error)
      verificationMap.set(userId, {
        profile: null, // ✅ AÑADIDO: Sin perfil en caso de error
        hasCompletedProfile: false,
        hasEssentialDocuments: false,
        hasGuarantor: false,
        guarantorInfo: null
      })
    }
  }

  // Actualizar el estado reactivo
  userVerificationData.value = new Map([...userVerificationData.value, ...verificationMap])
}

// Manejadores de navegación
const handleGoToContract = (contractId) => {
  router.push(`/contracts/${contractId}`)
}

const handleGoToColiving = (colivingId) => {
  router.push('/agent-dashboard/colivings')
}

const handleGoToVerifications = () => {
  router.push('/agent-dashboard/verifications')
}

// Nuevas funciones para las acciones de la sidebar
const handleSelectContract = (contractId) => {
  router.push(`/contracts/${contractId}`)
}

const handleViewCandidates = (contractId) => {
  router.push(`/contracts/${contractId}?tab=candidates`)
}





// NUEVA: Función para cargar eventos eliminados desde localStorage
const loadDismissedEvents = () => {
  try {
    const stored = localStorage.getItem('agent-dismissed-events')
    if (stored) {
      const parsed = JSON.parse(stored)
      dismissedEvents.value = new Set(parsed)
    }
  } catch (error) {
    console.warn('Error cargando eventos eliminados:', error)
    dismissedEvents.value = new Set()
  }
}

// NUEVA: Función para guardar eventos eliminados en localStorage
const saveDismissedEvents = () => {
  try {
    localStorage.setItem('agent-dismissed-events', JSON.stringify([...dismissedEvents.value]))
  } catch (error) {
    console.warn('Error guardando eventos eliminados:', error)
  }
}

// NUEVA: Función para eliminar un evento
const dismissEvent = (eventId) => {
  dismissedEvents.value.add(eventId)
  saveDismissedEvents()
}

// NUEVA: Función para limpiar todos los eventos eliminados (opcional)
const clearDismissedEvents = () => {
  dismissedEvents.value.clear()
  saveDismissedEvents()
}

// Función para verificar si el perfil del agente está completo
// Usa GET /api/agents/profile (ya resuelve el rol y los campos requeridos en backend)
const checkAgentProfileCompletion = async () => {
  try {
    const user = auth.currentUser
    if (!user) return

    const token = await user.getIdToken()
    const response = await fetch('/api/agents/profile', {
      headers: { Authorization: `Bearer ${token}` }
    })

    if (!response.ok) return

    const profileData = await response.json()
    profileIncomplete.value = profileData.profileComplete === false
  } catch (error) {
    console.error('Error al verificar perfil del agente:', error)
  }
}

// Cargar datos cuando se monta el componente
onMounted(async () => {
  initOnboardingAuth()

  // NUEVO: Cargar eventos eliminados desde localStorage
  loadDismissedEvents()
  
  setTimeout(async () => {
    await fetchAgentData()
    
    // Verificar si el perfil del agente está completo
    await checkAgentProfileCompletion()
    
    // NUEVO: Cargar información de verificación de usuarios después de cargar los datos
    const userIds = new Set()
    
    // Extraer IDs de usuarios de las aplicaciones de colivings
    colivings.value.forEach(coliving => {
      if (coliving.applications && Array.isArray(coliving.applications)) {
        coliving.applications.forEach(application => {
          if (application.applicantId) {
            userIds.add(application.applicantId)
          }
        })
      }
    })
    
    // ✅ ACTIVADO: Cargar información de verificación de usuarios
    if (userIds.size > 0) {
      await loadUserVerificationData(Array.from(userIds))
    }
  }, 500)
})

onUnmounted(() => {
  destroyOnboardingAuth()
})

// Refrescar progreso de onboarding al navegar (wizard + checklist)
watch(() => route.fullPath, () => {
  fetchOnboardingStatus()
})
</script>

<style scoped lang="postcss">
.router-link-exact-active {
  @apply bg-indigo-100 text-indigo-700;
}
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}
</style>